import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Autocomplete, Box, CircularProgress, Stack, Tab, TextField, Typography } from '@mui/material'
import { AdvancedMarker, APIProvider, Map } from '@vis.gl/react-google-maps'
import { t } from 'i18next'
import Joi from 'joi'
import { useEffect, useState } from 'react'

import { GOOGLE_MAPS_API_KEY } from '../../../configs/environment.js'
import useFieldValidation from '../../../helpers/fieldValidation.js'
import { useDevices } from '../Contexts/DevicesContext.jsx'

const schema = Joi.object({
  address: [Joi.string().optional(), Joi.allow(null)],
  city: [Joi.string().optional(), Joi.allow(null)],
  province: [Joi.string().optional(), Joi.allow(null)],
  region: [Joi.string().optional(), Joi.allow(null)],
  country: [Joi.string().optional(), Joi.allow(null)],
  position: {
    latitude: Joi.number().required(),
    longitude: Joi.number().required()
  }
})

const defaultCenter = {
  lat: 45.745,
  lng: 9.823
}

const containerStyle = {
  width: '100%',
  height: '280px'
}

const Position = ({ setIsValid, onChange }) => {
  const { useFetchPlaces } = useDevices()

  const [places, setPlaces] = useState([])
  const [isPlacesLoading, setIsPlacesLoading] = useState(false)

  const [validation, setValidation] = useState(false)
  const [position, setPosition] = useState({
    position: {
      latitude: defaultCenter.lat,
      longitude: defaultCenter.lng
    }
  })

  const [tab, setTab] = useState('0')

  const handleChangeTab = (_, value) => {
    if (position?.position === null)
      setPosition({
        ...position,
        position: {
          latitude: defaultCenter.lat,
          longitude: defaultCenter.lng
        }
      })

    setTab(value)
  }

  const onInputChange = (_, value) => {
    if (value?.length > 3) {
      setIsPlacesLoading(true)

      useFetchPlaces(value)
        .then((res) => {
          setPlaces(res.data)
        })
        .finally(() => {
          setIsPlacesLoading(false)
        })
    }
  }

  const onChangeAddress = (_, value) => {
    if (!value) {
      return
    }

    setPosition({
      ...position,
      address: value.address,
      city: value.city,
      province: value.province,
      region: value.region,
      country: value.country,
      position: {
        latitude: value.lat,
        longitude: value.lng
      }
    })
  }

  const onChangeLongitude = (event) => {
    setPosition({
      ...position,
      position: {
        ...position.position,
        [event.target.name]: parseFloat(event.target.value)
      }
    })
  }

  const onChangeLatitude = (event) => {
    setPosition({
      ...position,
      position: {
        ...position.position,
        [event.target.name]: parseFloat(event.target.value)
      }
    })
  }

  const handleMapClick = (event) => {
    setPosition({
      ...position,
      position: {
        latitude: Math.trunc(event.detail.latLng.lat * 1e7) / 1e7,
        longitude: Math.trunc(event.detail.latLng.lng * 1e7) / 1e7
      }
    })
  }

  useEffect(() => {
    const newValidation = useFieldValidation(position, schema)
    setValidation(newValidation)
    setIsValid(newValidation.isValid)
    onChange(position)
  }, [position])

  return (
    <Stack
      direction="column"
      spacing={5}
    >
      <Typography
        variant="h2"
        py={1}
      >
        {t('devices.addPosition')}
      </Typography>

      <TabContext value={tab}>
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={1}
          mt={4}
        >
          <Box sx={{
            borderBottom: 1,
            borderColor: 'divider'
          }}>
            <TabList
              onChange={handleChangeTab}
              aria-label="SelectPositionMethod"
            >
              <Tab
                label={t('common.findAddress')}
                value="0"
              />
              <Tab
                label={t('common.findCoordinates')}
                value="1"
              />
            </TabList>
          </Box>
        </Stack>

        <TabPanel
          value="0"
          sx={{ padding: '24px 0px' }}
        >
          <Autocomplete
            id="addDevice-position"
            name="address"
            loading={isPlacesLoading}
            options={places}
            getOptionLabel={(option) => option.address || position.address}
            filterOptions={(x) => x}
            isOptionEqualToValue={(option, value) => option.address === value}
            value={position.address}
            onChange={onChangeAddress}
            onInputChange={onInputChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('common.address')}
                variant="standard"
                slotProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isPlacesLoading && (
                        <CircularProgress
                          color="inherit"
                          size={20}
                        />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </TabPanel>

        <TabPanel
          value="1"
          sx={{ padding: '24px 0px' }}
        >
          <Stack
            direction="row"
            spacing={2}
            justifyContent="space-between"
          >
            <TextField
              label={t('common.latitude')}
              name={'latitude'}
              variant="standard"
              type="number"
              value={position?.position?.latitude || defaultCenter.lat}
              error={validation?.messages?.position?.length > 0}
              helperText={validation?.messages?.position?.join(', ')}
              fullWidth
              onChange={onChangeLatitude}
            />

            <TextField
              label={t('common.longitude')}
              name={'longitude'}
              variant="standard"
              type="number"
              value={position?.position?.longitude || defaultCenter.lng}
              error={validation?.messages?.position?.length > 0}
              helperText={validation?.messages?.position?.join(', ')}
              fullWidth
              onChange={onChangeLongitude}
            />
          </Stack>
        </TabPanel>
      </TabContext>

      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          mapId="position-map"
          style={containerStyle}
          defaultZoom={11}
          defaultCenter={{ lat: position.position.latitude, lng: position.position.longitude }}
          zoomControl
          onClick={handleMapClick}
          disableDefaultUI
        >
          <AdvancedMarker position={{ lat: position.position.latitude, lng: position.position.longitude }}>
          </AdvancedMarker>
        </Map>
      </APIProvider>
    </Stack >
  )
}

export default Position
